import type { ThemeConfig } from '../types/forms';
import { DEFAULT_THEME_CONFIG } from '../types/forms';

export const BUTTON_SIZE_PRESETS = {
  sm: { paddingX: 16, paddingY: 8,  fontSize: 14, minHeight: 40 },
  md: { paddingX: 24, paddingY: 12, fontSize: 16, minHeight: 52 },
  lg: { paddingX: 32, paddingY: 16, fontSize: 18, minHeight: 56 },
} as const;

export function getButtonStyles(
  themeConfig: ThemeConfig | undefined,
  variant: 'primary' | 'secondary' = 'primary',
  defaultSize: 'sm' | 'md' | 'lg' = 'md'
): React.CSSProperties {
  const theme = themeConfig || DEFAULT_THEME_CONFIG;

  const base: React.CSSProperties = {
    borderRadius: `${theme.buttonBorderRadius}px`,
    transition: 'all 200ms',
  };

  if (variant === 'secondary') {
    return { ...base, color: 'inherit' };
  }

  const size = theme.buttonSize ?? defaultSize;
  const preset = BUTTON_SIZE_PRESETS[size];
  const px = theme.buttonPaddingX ?? preset.paddingX;
  const py = theme.buttonPaddingY ?? preset.paddingY;

  const sizing: React.CSSProperties = {
    padding: `${py}px ${px}px`,
    fontSize: `${preset.fontSize}px`,
    minHeight: `${preset.minHeight}px`,
  };

  switch (theme.buttonStyle) {
    case 'outlined':
      return {
        ...base,
        ...sizing,
        backgroundColor: 'transparent',
        border: `2px solid ${theme.buttonBgColor}`,
        color: theme.buttonBgColor,
      };
    case 'ghost':
      return {
        ...base,
        ...sizing,
        backgroundColor: `${theme.buttonBgColor}15`,
        color: theme.buttonBgColor,
        border: 'none',
      };
    case 'filled':
    default:
      return {
        ...base,
        ...sizing,
        backgroundColor: theme.buttonBgColor,
        color: theme.buttonTextColor,
        border: 'none',
      };
  }
}

export function getInputStyles(
  themeConfig: ThemeConfig | undefined,
  state: { error?: boolean; focused?: boolean } = {}
): React.CSSProperties {
  const theme = themeConfig || DEFAULT_THEME_CONFIG;
  const style = theme.inputStyle || 'outlined';
  const bgColor = theme.inputBgColor || 'transparent';
  const borderRadius = theme.inputBorderRadius ?? 12;

  // Derive border color: explicit or 30% opacity of textColor
  const borderColor = theme.inputBorderColor || `${theme.textColor || '#ffffff'}4D`;
  const focusColor = theme.buttonBgColor || '#3b82f6';
  const errorColor = '#ef4444';

  const activeBorder = state.error
    ? errorColor
    : state.focused
      ? focusColor
      : borderColor;

  const base: React.CSSProperties = {
    color: theme.textColor || '#ffffff',
    transition: 'all 200ms',
    // CSS custom property for placeholder color
    ['--input-placeholder-color' as string]: theme.textColor || '#ffffff',
  };

  switch (style) {
    case 'underline':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: `2px solid ${activeBorder}`,
        borderRadius: 0,
        paddingLeft: 0,
        paddingRight: 0,
        ...(state.focused && !state.error ? {
          boxShadow: `0 2px 0 0 ${focusColor}`,
        } : {}),
      };

    case 'filled':
      return {
        ...base,
        backgroundColor: bgColor === 'transparent'
          ? `${theme.textColor || '#ffffff'}0D`
          : bgColor,
        border: 'none',
        borderBottom: `2px solid ${activeBorder}`,
        borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
        ...(state.focused && !state.error ? {
          borderBottomColor: focusColor,
        } : {}),
      };

    case 'outlined':
    default:
      return {
        ...base,
        backgroundColor: bgColor === 'transparent' ? 'transparent' : bgColor,
        border: `1px solid ${activeBorder}`,
        borderRadius: `${borderRadius}px`,
        ...(state.focused && !state.error ? {
          borderColor: focusColor,
          boxShadow: `0 0 0 3px ${focusColor}33`,
        } : {}),
      };
  }
}

export function getGoogleFontUrl(fontFamily: string): string | null {
  if (fontFamily === 'Inter') return null;
  const encoded = fontFamily.replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${encoded}:wght@300;400;500;600;700&display=swap`;
}
